/**
 * The shared "attach a file" control.
 *
 * One uploader, used everywhere a create surface lets a user add files before
 * the ticket exists: the AskIT composer, the inline request-offering form, and
 * the portal's dynamic `ATTACHMENT` parameter. It owns the *interaction* — the
 * hidden file input, the per-file chip/thumbnail, the uploading and failed
 * states, the remove affordance — and reports only the committed
 * `attachment_id`s upward through {@link AttachmentUploaderProps.onChange}.
 *
 * The bytes go up the moment a file is chosen, through the single
 * `useUploadDraftAttachment` path. Every safety check lives on the server; a
 * rejected upload comes back as one of `ATTACHMENT_TOO_LARGE`,
 * `ATTACHMENT_TYPE_NOT_ALLOWED` or `ATTACHMENT_NOT_CLEAN`, and is surfaced here
 * as translated copy via `useApiErrorMessage` rather than a raw code.
 *
 * Deliberately self-contained: it holds the `File`s and their previews in local
 * state and never takes an id list back in as a prop. Parents reset it by
 * changing its React `key` (see the composer's clear-on-send), which is what
 * keeps this component honest about which files are actually its own.
 *
 * Files get in three ways, and all three land on the same `addFiles` path: the
 * button (the primary, keyboard-reachable route), dropping onto the control,
 * and a screenshot pasted anywhere on the surrounding form, which arrives
 * through the imperative handle (see `usePasteToAttach`). Drag and paste are
 * *additions* and never the only way through — a pointer gesture cannot be
 * performed from a keyboard, and a paste needs a clipboard someone has already
 * filled, so removing the button would strand anyone who has neither.
 *
 * Dropping anywhere *else* in the window is a different problem, and not this
 * component's to solve: the browser's default for an unhandled drop is to
 * navigate to the file, which throws away everything the user has typed. That
 * is suppressed app-wide by `useFileDropGuard` (see `src/hooks`), which this
 * component cooperates with by calling `preventDefault()` on the drags it
 * accepts — the guard skips any event already claimed by a real drop zone.
 */

import { FileText, Icon, Paperclip, TriangleAlert, X } from "@norican/icons";
import { Button, Chip, cn, Spinner, Typography } from "@norican/react";
import {
  type DragEvent,
  type Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useApiErrorMessage } from "@/components/common";
import { useT } from "@/i18n";
import { useAttachmentMaxBytes, useUploadDraftAttachment } from "@/lib/api";
import { logger } from "@/lib/logger";

/**
 * Exactly what the server accepts (`DEFAULT_ATTACHMENT_CONTENT_TYPES` and the
 * `.msg`/`.eml` fallback in `backend/src/it_helpdesk_agent/config.py`), so the
 * picker never offers a file the upload will then refuse. It is still only a
 * hint — drops and pastes bypass it — so a rejection's copy is still handled.
 *
 * Not `image/*`: that offers BMP, TIFF, SVG and HEIC, none of which the server
 * takes. Listing the image types also makes iOS hand over an iPhone photo as
 * JPEG rather than HEIC. `.log` rides on `text/plain`, which the server allows
 * whatever the name; legacy `.ppt` and archives are refused server-side.
 */
export const ATTACHMENT_ACCEPT = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  "application/pdf",
  ".pdf",
  "text/plain",
  ".txt",
  ".log",
  "text/csv",
  ".csv",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".pptx",
  ".eml",
  ".msg",
].join(",");

/** Give up on an upload after this long, so a dead connection cannot hold Submit. */
const UPLOAD_TIMEOUT_MS = 120_000;

/** Bytes in a megabyte, as the server counts them for its limit. */
const BYTES_PER_MB = 1024 * 1024;

/** A byte limit as user copy: "10", or "2.5" for a fractional one. */
function megabytesLabel(bytes: number): string {
  return String(Math.round((bytes / BYTES_PER_MB) * 10) / 10);
}

/**
 * True only when the drag is carrying actual files.
 *
 * Dragging selected text, a link, or an image out of another tab all produce
 * drag events too, and accepting those would light the zone up for something it
 * can never take. `DataTransfer.files` is empty until the drop itself, so the
 * advertised `types` list is the only thing readable during `dragover` — the
 * spec puts the literal string `"Files"` in it when a file is in flight.
 *
 * Exported because `useFileDropGuard` has to ask the same question: it may only
 * suppress drags this app could conceivably have wanted, and dragging selected
 * text into a textarea is a native editing gesture that must keep working.
 */
export function dragCarriesFiles(transfer: DataTransfer | null): boolean {
  return transfer !== null && Array.from(transfer.types).includes("Files");
}

/** Imperative surface, so a form's paste handler can hand over a pasted image. */
export interface AttachmentUploaderHandle {
  addFiles: (files: readonly File[]) => void;
}

export interface AttachmentUploaderProps {
  /** React 19 passes this as an ordinary prop; no `forwardRef` wrapper needed. */
  ref?: Ref<AttachmentUploaderHandle>;
  /** Committed `attachment_id`s, newest last. Fired whenever an upload settles. */
  onChange: (attachmentIds: string[]) => void;
  /**
   * The same committed files with their names, for a parent that has to show
   * them somewhere else (the chat carries them onto a request form card).
   */
  onFilesChange?: (files: { attachmentId: string; name: string }[]) => void;
  /** Notified while at least one upload is in flight, so a parent can hold Submit. */
  onPendingChange?: (pending: boolean) => void;
  /** Locks the trigger and the remove buttons (e.g. while the form is submitting). */
  disabled?: boolean;
  /** Allow choosing several files at once. Defaults to `true`. */
  multiple?: boolean;
  /** The file picker's `accept` hint. Defaults to {@link ATTACHMENT_ACCEPT}. */
  accept?: string;
  /** Overrides the trigger label. Defaults to the shared "Attach files" copy. */
  triggerLabel?: string;
  /** Ties the control to an external `<label>`/field for assistive tech. */
  id?: string;
  className?: string;
}

type UploadStatus = "uploading" | "done" | "error";

interface UploadItem {
  localId: string;
  name: string;
  isImage: boolean;
  /** Object URL for image previews; revoked on removal and unmount. */
  previewUrl: string | null;
  status: UploadStatus;
  attachmentId: string | null;
  errorMessage: string | null;
  /** A failed upload (not a refused one) that "Try again" can resend. */
  retryable: boolean;
}

export function AttachmentUploader({
  ref,
  onChange,
  onFilesChange,
  onPendingChange,
  disabled = false,
  multiple = true,
  accept = ATTACHMENT_ACCEPT,
  triggerLabel,
  id,
  className,
}: AttachmentUploaderProps) {
  const { t } = useT();
  const { t: tErrors } = useT("errors");
  const resolveError = useApiErrorMessage();
  const upload = useUploadDraftAttachment();
  const maxBytes = useAttachmentMaxBytes();
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [dropActive, setDropActive] = useState(false);

  /**
   * How many nested elements the pointer is currently inside.
   *
   * `dragenter` and `dragleave` fire for every element boundary crossed, not
   * just the zone's own — so moving from the zone onto the button inside it
   * fires `dragleave` (zone) *and* `dragenter` (button), in that order. Toggling
   * the highlight straight off a `dragleave` makes it strobe while the pointer
   * is still comfortably inside. Counting enters against leaves means the
   * highlight only drops when the count returns to zero, i.e. when the pointer
   * has genuinely left the outermost element.
   */
  const dragDepth = useRef(0);

  // Callbacks are read through refs so the effects below can fire on a real
  // change of ids/pending state without re-running every time a parent passes
  // a fresh inline function.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onFilesRef = useRef(onFilesChange);
  onFilesRef.current = onFilesChange;
  const onPendingRef = useRef(onPendingChange);
  onPendingRef.current = onPendingChange;

  const committedIds = useMemo(
    () =>
      items
        .filter((item) => item.status === "done" && item.attachmentId !== null)
        .map((item) => item.attachmentId as string),
    [items],
  );
  const pending = useMemo(() => items.some((item) => item.status === "uploading"), [items]);

  const lastSentIds = useRef<string>("");
  useEffect(() => {
    const key = committedIds.join(",");
    if (key !== lastSentIds.current) {
      lastSentIds.current = key;
      onChangeRef.current(committedIds);
      onFilesRef.current?.(
        items
          .filter((item) => item.status === "done" && item.attachmentId !== null)
          .map((item) => ({ attachmentId: item.attachmentId as string, name: item.name })),
      );
    }
  }, [committedIds, items]);

  const lastPending = useRef(false);
  useEffect(() => {
    if (pending !== lastPending.current) {
      lastPending.current = pending;
      onPendingRef.current?.(pending);
    }
  }, [pending]);

  // Free every object URL on unmount so image previews do not leak.
  useEffect(
    () => () => {
      setItems((prev) => {
        prev.forEach((item) => item.previewUrl !== null && URL.revokeObjectURL(item.previewUrl));
        return prev;
      });
    },
    [],
  );

  /* The `File` behind each upload that can fail, so a failed one can be retried
     without asking the person to find the file again. */
  const filesRef = useRef(new Map<string, File>());

  const patchItem = (localId: string, patch: Partial<UploadItem>) => {
    setItems((prev) =>
      prev.map((item) => (item.localId === localId ? { ...item, ...patch } : item)),
    );
  };

  const startUpload = (item: UploadItem, file: File) => {
    filesRef.current.set(item.localId, file);
    /* A stalled upload must fail rather than spin: "Uploading…" holds the
       form's Submit, so one dead connection would block filing for good. */
    const signal = AbortSignal.timeout(UPLOAD_TIMEOUT_MS);
    upload
      .mutateAsync({ file, signal })
      .then((attachment) => {
        logger.debug("[attachments] uploaded draft", attachment.attachment_id);
        filesRef.current.delete(item.localId);
        patchItem(item.localId, { status: "done", attachmentId: attachment.attachment_id });
      })
      .catch((error: unknown) => {
        logger.warn("[attachments] draft upload failed", item.name, error);
        patchItem(item.localId, {
          status: "error",
          retryable: true,
          errorMessage: signal.aborted ? t("attachment.timedOut") : resolveError(error).description,
        });
      });
  };

  /** Only an upload that was attempted can be retried (not a refused size). */
  const retryItem = (item: UploadItem) => {
    const file = filesRef.current.get(item.localId);
    if (file === undefined) {
      return;
    }
    patchItem(item.localId, { status: "uploading", errorMessage: null, retryable: false });
    startUpload(item, file);
  };

  const addFiles = (files: readonly File[]) => {
    if (files.length === 0) {
      return;
    }
    const chosen = files.map((file) => {
      // Refused here rather than sent: the server would only answer 413 after
      // the whole file had crossed the network.
      const tooLarge = file.size > maxBytes;
      const isImage = file.type.startsWith("image/");
      const item: UploadItem = {
        localId: crypto.randomUUID(),
        name: file.name,
        isImage,
        previewUrl: isImage && !tooLarge ? URL.createObjectURL(file) : null,
        status: tooLarge ? "error" : "uploading",
        attachmentId: null,
        errorMessage: tooLarge
          ? tErrors("code.ATTACHMENT_TOO_LARGE", { max_mb: megabytesLabel(maxBytes) })
          : null,
        retryable: false,
      };
      if (tooLarge) {
        logger.debug("[attachments] refused before upload: too large", file.name, file.size);
      }
      return { item, file, tooLarge };
    });
    setItems((prev) => [...prev, ...chosen.map((entry) => entry.item)]);
    chosen
      .filter((entry) => !entry.tooLarge)
      .forEach((entry) => startUpload(entry.item, entry.file));
  };

  const handleFiles = (fileList: FileList | null) => {
    addFiles(fileList === null ? [] : Array.from(fileList));
  };

  /* Lets a surface's paste handler hand over an image pasted anywhere on the
     form. The upload, the thumbnail, the failure state and the id plumbing are
     all here already; paste only needs a way in. */
  useImperativeHandle(ref, () => ({ addFiles }));

  const removeItem = (localId: string) => {
    setItems((prev) => {
      const target = prev.find((item) => item.localId === localId);
      if (target?.previewUrl != null) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((item) => item.localId !== localId);
    });
    filesRef.current.delete(localId);
  };

  const openPicker = () => {
    inputRef.current?.click();
  };

  const endDrag = () => {
    dragDepth.current = 0;
    setDropActive(false);
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (disabled || !dragCarriesFiles(event.dataTransfer)) {
      return;
    }
    // Claims the drag for this zone: `useFileDropGuard` leaves any event that
    // is already `defaultPrevented` alone.
    event.preventDefault();
    dragDepth.current += 1;
    setDropActive(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (disabled || !dragCarriesFiles(event.dataTransfer)) {
      return;
    }
    // Without this the drop event never fires at all — the single most common
    // reason a hand-written drop zone silently does nothing.
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    // Belt and braces with the depth counter: `relatedTarget` is the element
    // being entered, so a leave that lands on one of our own descendants is the
    // pointer moving *within* the zone and must not clear the highlight. It is
    // null when the pointer leaves the window entirely, which is a real leave.
    const entering = event.relatedTarget;
    if (entering instanceof Node && event.currentTarget.contains(entering)) {
      return;
    }
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) {
      setDropActive(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    // Unconditional, including while disabled: anything less and the browser
    // falls back to opening the dropped file and the page is gone.
    event.preventDefault();
    endDrag();
    if (disabled || !dragCarriesFiles(event.dataTransfer)) {
      return;
    }
    const dropped = Array.from(event.dataTransfer.files);
    // Nothing is filtered against `accept` here. That attribute is a picker
    // hint, not a rule, and the server's allowlist is the real gate — silently
    // swallowing a dropped file would leave the user staring at a zone that
    // accepted their gesture and produced nothing. A rejected upload comes back
    // as translated copy on the chip instead.
    logger.debug("[attachments] files dropped", dropped.length);
    addFiles(multiple ? dropped : dropped.slice(0, 1));
  };

  return (
    /* The whole control is the drop zone, so the chips, the thumbnails and the
       button are all valid targets rather than only the button. The drag-over
       state is drawn with an `outline` (plus offset) rather than a border or
       padding on purpose: outlines are painted outside the box and cost no
       layout, so the target cannot shift out from under the pointer at the
       moment it lights up.

       No drag handler is attached to any interactive child — the events bubble,
       and one set of handlers on the root is what makes the depth counter
       meaningful. */
    <div
      className={cn(
        "flex flex-col gap-3 rounded-md",
        dropActive && "bg-accent-subtle outline-accent-border outline-2 outline-offset-4",
        className,
      )}
      data-drop-active={dropActive ? "true" : undefined}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {items.length === 0 ? null : (
        <ul className="flex flex-wrap gap-2">
          {items.map((item) =>
            item.isImage ? (
              <li key={item.localId}>
                <ImageThumb
                  item={item}
                  disabled={disabled}
                  onRemove={() => removeItem(item.localId)}
                />
              </li>
            ) : (
              <li key={item.localId}>
                <Chip
                  tone={item.status === "error" ? "danger" : "neutral"}
                  startIcon={
                    item.status === "uploading" ? (
                      <Spinner size="sm" label={t("attachment.uploading")} />
                    ) : (
                      <Icon
                        icon={item.status === "error" ? TriangleAlert : FileText}
                        size="sm"
                        tone={item.status === "error" ? "danger" : undefined}
                      />
                    )
                  }
                  onRemove={disabled ? undefined : () => removeItem(item.localId)}
                  removeLabel={t("attachment.remove", { name: item.name })}
                  title={item.errorMessage ?? item.name}
                >
                  {item.name}
                </Chip>
              </li>
            ),
          )}
        </ul>
      )}

      {/* Always mounted, so a failure that lands later is announced. */}
      <div aria-live="polite" className="flex flex-col gap-1 empty:hidden">
        {items
          .filter((item) => item.status === "error")
          .map((item) => (
            <div key={item.localId} className="flex flex-wrap items-center gap-x-2">
              <Typography variant="caption" tone="danger">
                {item.name}: {item.errorMessage ?? t("attachment.failed")}
              </Typography>
              {disabled || !item.retryable ? null : (
                <Button
                  type="button"
                  variant="ghost"
                  tone="neutral"
                  size="sm"
                  aria-label={t("attachment.retryNamed", { name: item.name })}
                  onClick={() => retryItem(item)}
                >
                  {t("button.retry")}
                </Button>
              )}
            </div>
          ))}
      </div>

      {/* The button stays the primary path and is the only one a keyboard can
          reach; the hint beside it is what makes the drop target discoverable
          at all, since a drag affordance is invisible until someone already
          knows to try it. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Button
          type="button"
          variant="outline"
          tone="neutral"
          size="sm"
          disabled={disabled}
          startIcon={<Icon icon={Paperclip} size="sm" />}
          onClick={openPicker}
        >
          {triggerLabel ?? t("attachment.add")}
        </Button>

        {disabled ? null : (
          <Typography variant="caption" tone={dropActive ? "accent" : "muted"}>
            {dropActive ? t("attachment.dropNow") : t("attachment.dropHint")}
          </Typography>
        )}
        <input
          ref={inputRef}
          id={id}
          type="file"
          multiple={multiple}
          accept={accept}
          className="hidden"
          onChange={(event) => {
            handleFiles(event.target.files);
            // Reset so re-choosing the same file fires `change` again.
            event.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

interface ImageThumbProps {
  item: UploadItem;
  disabled: boolean;
  onRemove: () => void;
}

/** A square image preview with an uploading/failed overlay and a remove button. */
function ImageThumb({ item, disabled, onRemove }: ImageThumbProps) {
  const { t } = useT();

  return (
    <div className="border-border-subtle relative h-16 w-16 overflow-hidden rounded-md border">
      {item.previewUrl === null ? null : (
        <img src={item.previewUrl} alt={item.name} className="h-full w-full object-cover" />
      )}

      {item.status === "uploading" ? (
        <div className="bg-surface/70 absolute inset-0 flex items-center justify-center">
          <Spinner size="sm" label={t("attachment.uploading")} />
        </div>
      ) : null}

      {item.status === "error" ? (
        <div className="bg-danger-subtle/80 absolute inset-0 flex items-center justify-center">
          <Icon
            icon={TriangleAlert}
            size="sm"
            tone="danger"
            label={item.errorMessage ?? undefined}
          />
        </div>
      ) : null}

      {disabled ? null : (
        <Button
          type="button"
          variant="solid"
          tone="neutral"
          size="sm"
          iconOnly
          aria-label={t("attachment.remove", { name: item.name })}
          // Drawn at 20 px so it does not cover the preview, but the `before:`
          // layer widens the tap target to 32 px. It is inset 6 px so that
          // larger area stays inside the thumbnail's `overflow-hidden`.
          className="absolute top-1.5 right-1.5 h-5 min-h-0 w-5 rounded-full p-0 before:absolute before:-inset-1.5 before:content-['']"
          onClick={onRemove}
        >
          <Icon icon={X} size="xs" />
        </Button>
      )}
    </div>
  );
}
